import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { SUBDOMAIN } from "../../../assets/meta/meta";
import { API, BACKEND_HOST } from "../../../api";
import { getAccessToken } from "../../../api/auth";
import { useAuthContext } from "../../../Components/AuthContext";
import CouponReservationSelectModal from "../../../Components/modal/CouponReservationSelectModal";
import { calcCouponSaleAmount } from "../../../common/utils/coupon_util";
import { processOrderFreeBooking } from "../../../api/services/package.service";
import { usePGModules, useTossPaymentWidget, useOrderAgreements, dispatchPayment } from "../../../common/payments";
import { CollapsibleSection, OrdererInfoSection, RefundPolicySection, PGSelectorSection, PayButton } from "../components";
import { Button } from "../../../Components/ui/button";
import { calculatePackageOrderTotal, isFreeSeatPackage } from "./packageOrderSummary.mjs";
import { ChevronRight } from "lucide-react";

const KOREAN_WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const formatScheduleDate = (dateString) => {
  if (!dateString) return "-";
  const [year, month, day] = String(dateString).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return `${year}. ${month}. ${day}. (${KOREAN_WEEKDAYS[date.getDay()]})`;
};

const OrderProcess = ({ onProgressChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAuthContext();

  // --- 공통 훅 ---
  const { pgModules, selectedPG, setSelectedPG } = usePGModules();
  const {
    cancelRuleAgree, purchaseAllAgree, purchaseOneAgree,
    setCancelRuleAgree, togglePurchaseAgreeOptions, allAgreed,
  } = useOrderAgreements();

  const [paying, setPaying] = useState(false);

  // --- 쿠폰 ---
  const [couponModal, setCouponModal] = useState(false);
  const [selectCoupon, setSelectCoupon] = useState([]);
  const [targetSeat, setTargetSeat] = useState({});

  const startDate = location.state.startDate || location.state.selectDate;
  const endDate = location.state.endDate || startDate;
  const hasLodgingDateRange = Boolean(endDate && endDate !== startDate);
  const selectedSpots = location.state.spotId || [];
  const isFreeSeatOrder = isFreeSeatPackage(location.state.product);
  const privateOfferToken = location.state.private_offer_token || location.state.product?.private_offer_token || "";
  const privateOfferItemId = location.state.private_offer_item_id || location.state.product?.private_offer_item_id || "";

  // --- 가격 ---
  const [packagePrice, setPackagePrice] = useState(location.state.product.price);
  useEffect(() => {
    setPackagePrice(location.state.product.price);
    if (!hasLodgingDateRange) return;

    API.get(`/package/get-lodge-package-price/?package_id=${location.state.product.id}&start_date=${startDate}&end_date=${endDate}`).then((response) => {
      setPackagePrice(response.data.price);
    }).catch((error) => {
      console.log("get-lodge-package-price", error);
    });
  }, [endDate, hasLodgingDateRange, location.state.product.id, location.state.product.price, startDate]);

  const toggleReservationDiscountCouponSelection = (product_id, coupon_id, spot_id, discount_amount, discount_type, min_purchase, discount_rate, max_discount = 0, coupon_title = "") => {
    setSelectCoupon((prev) => {
      let _prev = [...prev];
      const item = { coupon_id, product_id, spot_id, discount_amount, discount_type, min_purchase, discount_rate, max_discount, coupon_title };
      const selected = _prev.find((i) => i.product_id === product_id && i.coupon_id === coupon_id && i.spot_id === spot_id);
      const blocked = _prev.find((i) => i.product_id === product_id && i.coupon_id === coupon_id && i.spot_id !== spot_id);
      if (blocked) return _prev;
      _prev = _prev.filter((i) => i.spot_id !== spot_id);
      return selected ? _prev : [..._prev, item];
    });
  };

  const optionsPrice = location.state.options
    .map((item) => parseInt(item.price))
    .reduce((sum, curr) => sum + curr, 0);

  const totalOrderPrice = useMemo(() => {
    return calculatePackageOrderTotal({
      product: location.state.product,
      spots: selectedSpots,
      packagePrice,
      optionsPrice,
      coupons: privateOfferToken ? [] : selectCoupon,
    });
  }, [selectCoupon, privateOfferToken, location.state.product, selectedSpots, optionsPrice, packagePrice]);

  const totalTaxfreePrice = location.state.options
    .map((item) => (item.taxfree ? parseInt(item.price) : 0))
    .reduce((sum, curr) => sum + curr, 0);
  const formatPrice = (value) => Number(value || 0).toLocaleString();
  const selectedPackageCoupons = privateOfferToken ? [] : selectCoupon.filter((coupon) => coupon.product_id === location.state.product.id);
  const couponDiscountAmount = selectedPackageCoupons.reduce((sum, coupon) => {
    return sum + calcCouponSaleAmount(coupon, packagePrice);
  }, 0);
  const hasAppliedCoupon = couponDiscountAmount > 0;
  const appliedCouponTitle = selectedPackageCoupons.map((coupon) => coupon.coupon_title).filter(Boolean).join(", ");
  const productOrderAmount = totalOrderPrice + couponDiscountAmount;
  const sortOptionsByKoreanName = (options) => [...options].sort((a, b) => {
    const nameCompare = String(a.name || "").localeCompare(String(b.name || ""), "ko-KR");
    if (nameCompare !== 0) return nameCompare;
    return Number(a.price || 0) - Number(b.price || 0);
  });
  const groupOptionsByNameAndPrice = (options) => Array.from(options.reduce((map, option) => {
    const price = Number(option.price || 0);
    const key = `${option.optionId || option.id || option.name}-${price}`;
    const current = map.get(key) || {
      name: option.name,
      price,
      quantity: 0,
      totalPrice: 0,
    };

    current.quantity += 1;
    current.totalPrice += price;
    map.set(key, current);
    return map;
  }, new Map()).values());

  useEffect(() => {
    onProgressChange?.(100);
  }, [onProgressChange]);

  // --- Toss 위젯 ---
  const { paymentWidgetRef } = useTossPaymentWidget({
    userInfo,
    totalOrderPrice,
    isTossSelected: selectedPG?.code === "toss",
  });

  // --- 결제 ---
  const processPayment = async () => {
    setPaying(true);
    let shouldKeepPaying = false;
    try {
      const accessToken = getAccessToken();
      if (!userInfo) {
        toast.error("로그인이 필요한 서비스입니다.");
        navigate(`/login`);
        return;
      }
      if (totalOrderPrice === 0 && !location.state.product.allow_free_booking) {
        toast.error("결제가능한 최소금액은 1,000원입니다.");
        return;
      }

      const _data = {
        spots: location.state.spotId,
        startDate,
        endDate,
        options: location.state.options.map((item) => ({ spot_id: item.spotId, option_id: item.optionId, seat_key: item.seatKey })),
        coupons: privateOfferToken ? [] : selectCoupon.map((item) => ({ coupon_id: item.coupon_id, spot_id: item.spot_id })),
        membership_product_id: privateOfferToken ? "" : location.state.membership_product_id,
        private_offer_token: privateOfferToken,
        private_offer_item_id: privateOfferItemId,
      };

      const response = await API.post(`package/pre-order/`, _data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = response.data;

      if ("error" in data) {
        toast.error(data.error);
        if (hasLodgingDateRange) {
          navigate(`/lodging-package/`, { state: { startDate, endDate, packageId: location.state.product.id, selectedSpotId: location.state.spotId } });
        } else {
          navigate(`/package/${location.state.product.id}?date=${startDate}`, { state: { selectedSpotId: location.state.spotId } });
        }
        return;
      }

      // 무료예약
      if (totalOrderPrice === 0 && location.state.product.allow_free_booking) {
        await processOrderFreeBooking(data.orderId, totalOrderPrice);
        toast.success("예약이 완료되었습니다.");
        navigate(`/my-page`);
        return;
      }

      const paymentResult = await dispatchPayment({
        selectedPG,
        paymentWidgetRef,
        orderId: data.orderId,
        orderName: location.state.product.name,
        amount: totalOrderPrice,
        userInfo,
        taxFreeAmount: totalTaxfreePrice,
        urls: {
          successUrl: `${BACKEND_HOST}/package/order/`,
          failUrl: `https://${SUBDOMAIN}.${window.AMZ_ENV.VITE_DOMAIN}/reservation`,
          niceReturnUrl: "/package/order-nice/",
          hectoNotiUrl: `${BACKEND_HOST}/package/order-hecto/`,
          hectoNextUrl: `${BACKEND_HOST}/package/order-done-hecto/`,
          hectoCancUrl: `${BACKEND_HOST}/package/order-fail-hecto/`,
        },
        failNavigate: () => navigate(`/reservation`),
      });
      shouldKeepPaying = paymentResult.redirectRequested;
    } catch (error) {
      console.log(error);
      if (error.response) {
        toast.error(`결제에 실패하였습니다. ${error.response.data?.error || String(error.response.data)}`);
      } else {
        toast.error("지금은 구매가능 시간이 아니거나 품절된 상품입니다.");
      }
      navigate(`/reservation`);
    } finally {
      if (!shouldKeepPaying) {
        setPaying(false);
      }
    }
  };

  return (
    <>
      <div className="space-y-2.5">
          {/* 주문자 */}
          <OrdererInfoSection userInfo={userInfo} />

          {/* 주문상품 */}
          <CollapsibleSection title="주문상품" defaultOpen>
            <div className="flex flex-col gap-4">
            <div>
              <div className="rounded-2xl border border-gray-700 px-6 py-4">
              <div className="space-y-2">
                <div className="font-gmarket text-base font-bold text-gray-900">
                  {location.state.product.name}
                </div>
                <div className="text-sm font-semibold text-gray-700">
                  일정 {formatScheduleDate(startDate)}
                </div>
                {location.state.product.site ? (
                  <div className="text-xs font-medium text-gray-400">
                    {location.state.product.site}
                  </div>
                ) : null}
              </div>
              </div>
            </div>

            {selectedSpots && (
              <div className="my-3">
                <div className="mb-2 text-sm font-bold text-gray-900">좌석 및 옵션</div>
                {isFreeSeatOrder ? (
                  <div className="space-y-2">
                    {selectedSpots.map((spot, idx) => {
                      const _options = location.state.options.filter((v) => v.seatKey === spot.clientKey);
                      const groupedOptions = groupOptionsByNameAndPrice(_options);
                      const sortedGroupedOptions = sortOptionsByKoreanName(groupedOptions);
                      const seatOptionsTotal = groupedOptions.reduce((sum, option) => sum + option.totalPrice, 0);
                      const seatTotal = packagePrice + seatOptionsTotal;

                      return (
                        <div key={spot.clientKey || idx} className="rounded-xl border border-gray-200 bg-white px-3 py-3">
                          <div className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
                            <div className="bg-gray-50">
                              <span className="font-gmarket text-sm font-bold text-gray-900">자유석 {idx + 1}</span>
                              {Number(packagePrice || 0) > 0 ? (
                                <div className="mt-1 text-xs text-gray-400">
                                  기본 {formatPrice(packagePrice)}원
                                </div>
                              ) : null}
                            </div>
                            <span className="text-sm font-bold text-gray-900">{formatPrice(seatTotal)}원</span>
                          </div>

                          {groupedOptions.length ? (
                            <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-2.5">
                              {sortedGroupedOptions.map((option) => (
                                <div key={`${spot.clientKey || idx}-${option.name}-${option.price}`} className="flex items-center justify-between px-4 text-xs text-gray-900">
                                  <span>
                                    {option.name}
                                    <span className="ml-1 text-gray-900">x{option.quantity}</span>
                                  </span>
                                  <span>{formatPrice(option.totalPrice)}원</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-3 border-t border-gray-100 pt-2.5 text-xs text-gray-900">선택한 옵션 없음</div>
                          )}
                        </div>
                      );
                    })}

                  </div>
                ) : location.state.spotId.map((spot, idx) => {
                  const _options = location.state.options.filter((v) => v.spotId === spot.id);
                  const groupedOptions = groupOptionsByNameAndPrice(_options);
                  const sortedOptions = sortOptionsByKoreanName(groupedOptions);
                  return (
                    <div key={idx} className="mb-2  bg-white px-3 py-3 last:mb-0">
                      <div className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2">
                        <span className="font-gmarket text-sm font-bold text-[#e16036]">{spot.name}번</span>
                        <span className="text-sm font-bold text-gray-900">{formatPrice(packagePrice)}원</span>
                      </div>
                      <div className="mt-2 px-4  space-y-1">
                        {sortedOptions.length ? sortedOptions.map((option) => (
                          <div key={`${spot.id}-${option.name}-${option.price}`} className="flex items-center justify-between text-xs text-gray-900">
                            <span>
                              {option.name}
                              <span className="ml-1 text-gray-900">x{option.quantity}</span>
                            </span>
                            <span>{formatPrice(option.totalPrice)}원</span>
                          </div>
                        )) : (
                          <div className="text-xs text-gray-900">선택한 옵션 없음</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            </div>
          </CollapsibleSection>

          {selectedSpots?.length && !privateOfferToken ? (
            <CollapsibleSection
              title={(
                <span className="inline-flex items-center gap-2">
                  할인
                  {hasAppliedCoupon ? (
                    <span className="rounded-sm bg-[#fff1ec] px-2 py-1 text-xs font-bold text-[#e16036]">
                      적용중
                    </span>
                  ) : null}
                </span>
              )}
              fixed
            >
              <div className="flex items-center justify-between gap-3 rounded-2xl bg-gray-50 px-4 py-4">
                <div className="min-w-0">
                  {hasAppliedCoupon ? (
                    <span className="block text-sm font-bold text-[#e16036]">
                      {appliedCouponTitle || "쿠폰 적용"}
                    </span>
                  ) : (
                    <span className="block text-sm font-semibold text-gray-500">적용된 쿠폰 없음</span>
                  )}
                </div>
                <Button
                  variant={hasAppliedCoupon ? "ghost" : "outline"}
                  size="sm"
                  className={hasAppliedCoupon
                    ? "border-0  px-3 py-2 text-sm font-bold text-[#e16036]"
                    : undefined}
                  onClick={() => {
                    const spot = selectedSpots[0];
                    setCouponModal(true);
                    setTargetSeat({
                      spot_id: spot?.id,
                      product_id: location.state.product.id,
                      price: packagePrice,
                      options: location.state.options,
                      reserve_date: startDate,
                      start_date: startDate,
                      end_date: endDate,
                    });
                  }}
                >
                  {hasAppliedCoupon ? (
                    <>
                      - {formatPrice(couponDiscountAmount)}원
                      <ChevronRight size={16} strokeWidth={2.4} />
                    </>
                  ) : "쿠폰 선택"}
                </Button>
              </div>
            </CollapsibleSection>
          ) : null}

          <CollapsibleSection title="결제금액" fixed>
            <div className="rounded-2xl bg-gray-50 px-4 py-4">
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between gap-4 text-gray-900">
                  <span>상품 금액</span>
                  <span className="font-semibold text-gray-900">{formatPrice(productOrderAmount)}원</span>
                </div>
                <div className="flex items-center justify-between gap-4 text-gray-900">
                  <span>할인 금액</span>
                  <span className={hasAppliedCoupon ? "font-semibold text-[#e16036]" : "font-semibold text-gray-900"}>
                    {hasAppliedCoupon ? "-" : ""}{formatPrice(couponDiscountAmount)}원
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-4 border-t border-gray-200 pt-3">
                  <span className="text-base font-bold text-gray-900">총결제금액</span>
                  <span className="text-lg font-bold text-[#e16036]">{formatPrice(totalOrderPrice)}원</span>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* 환불 규정 */}
          <RefundPolicySection
            type="package"
            cancelRuleAgree={cancelRuleAgree}
            purchaseOneAgree={purchaseOneAgree}
            onCancelRuleChange={setCancelRuleAgree}
            onPurchaseAgreeChange={togglePurchaseAgreeOptions}
          />

          {/* PG 선택 */}
          <PGSelectorSection pgModules={pgModules} selectedPG={selectedPG} onSelect={setSelectedPG} />

          {/* 하단 고정 결제 버튼 여백 */}
          <div style={{ height: "100px" }} />
      </div>

      {/* 결제 버튼 */}
      <PayButton
        amount={totalOrderPrice}
        loading={paying}
        onClick={() => {
          if (!allAgreed) { toast.warning("동의항목을 확인해주세요."); return; }
          processPayment();
        }}
      />

      <CouponReservationSelectModal
        setVisible={setCouponModal}
        visible={couponModal}
        seats={targetSeat}
        selectCoupon={selectCoupon}
        setSelectCoupon={setSelectCoupon}
        toggleReservationDiscountCouponSelection={toggleReservationDiscountCouponSelection}
        navigate={navigate}
      />
    </>
  );
};

export default OrderProcess;
